import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Close } from '@mui/icons-material';
import NumberInput from '../utils/NumberInput';
import CriteriaIconImg from './CriteriaIconImg';
import CriterionWeight from './CriterionWeight';
import WeightIcon from '../utils/WeightIcon';
import './MobileSheetCriteria.css';

const TXT_EMPTY = { fr: 'Aucun critère sélectionné', en: 'No criteria selected', de: 'Keine Kriterien ausgewählt', es: 'Ningún criterio seleccionado', pt: 'Nenhum critério selecionado' };
const TXT_REMOVE = { fr: 'Retirer', en: 'Remove', de: 'Entfernen', es: 'Quitar', pt: 'Remover' };
const TXT_MAXIMIZE = { fr: 'Maximiser', en: 'Maximize', de: 'Maximieren', es: 'Maximizar', pt: 'Maximizar' };
const TXT_SET_TARGET = { fr: 'Définir une cible', en: 'Set a target', de: 'Ziel festlegen', es: 'Definir un objetivo', pt: 'Definir um alvo' };
const TXT_DISPLAY_WEIGHTS = { fr: 'Afficher les poids', en: 'Display weights', de: 'Gewichtungen anzeigen', es: 'Mostrar los pesos', pt: 'Exibir os pesos' };
const INFINITY = String.fromCharCode(0x221E);

// Per-stat target editor shown in the gen bar: a numeric target field plus an
// ∞ toggle. ∞ on = maximize (field disabled/grayed); ∞ off = specific target.
const StatTargetEditor = ({ criterion, lang, disabled = false }) => {
    const isMax = !!criterion.isMaximize;
    const fieldRef = useRef(null);
    // Set when the user taps the (disabled) field in ∞ mode: once we've switched
    // out of maximize and the input is enabled, focus + select it so they can
    // type a target straight away.
    const pendingFocus = useRef(false);

    useEffect(() => {
        if (!pendingFocus.current || isMax) return undefined;
        pendingFocus.current = false;
        const input = fieldRef.current?.querySelector('input');
        if (!input) return undefined;
        const raf = requestAnimationFrame(() => {
            input.focus();
            if (typeof input.select === 'function') input.select();
        });
        return () => cancelAnimationFrame(raf);
    }, [isMax]);

    // Tapping the field while in ∞ mode drops maximize (turning off the +∞
    // button) and hands focus to the numeric input — a one-tap path out of the
    // default maximize state.
    const activateField = () => {
        if (disabled || !isMax) return;
        pendingFocus.current = true;
        criterion.onToggleMaximize?.(false);
    };

    return (
        <span className="mobile-sheet-list__target-edit row">
            <button
                type="button"
                className={`mobile-sheet-list__inf${isMax ? ' is-on' : ''}`}
                aria-pressed={isMax}
                aria-label={TXT_MAXIMIZE[lang] || TXT_MAXIMIZE.en}
                onClick={() => criterion.onToggleMaximize?.(!isMax)}
                disabled={disabled}
            >
                +{INFINITY}
            </button>
            <span className="mobile-sheet-list__field" ref={fieldRef}>
                <NumberInput
                    value={Number.isFinite(criterion.targetValue) ? criterion.targetValue : (criterion.minValue ?? 0)}
                    onValueChange={(v) => criterion.onSetTarget?.(v)}
                    min={criterion.minValue ?? 0}
                    max={criterion.maxValue ?? 9999}
                    size={4}
                    allowNegative={false}
                    disabled={disabled || isMax}
                />
                {isMax && !disabled ? (
                    <button
                        type="button"
                        className="mobile-sheet-list__field-tap"
                        aria-label={TXT_SET_TARGET[lang] || TXT_SET_TARGET.en}
                        onClick={activateField}
                    />
                ) : null}
            </span>
        </span>
    );
};

// Split a criterion label like "Force ≥ 300" into its name and target parts so
// each can be styled on its own. A pre-computed targetLabel (e.g. ∞ for a
// maximize target) wins over an inline threshold when present.
const splitCriterionLabel = (criterion) => {
    const label = String(criterion?.label || '');
    const parsed = label.match(/^(.*?)(\s*(?:>=|≥)\s*.+)$/);
    const name = parsed ? parsed[1].trim() : label;
    const inlineTarget = parsed ? parsed[2].trim() : null;
    return { name, target: criterion?.targetLabel || inlineTarget };
};

const iconClassName = (criterion) => (
    `mobile-sheet-criteria__icon${criterion?.kind ? ` mobile-sheet-criteria__icon--${criterion.kind}` : ''}`
);

const withLineIndicator = (criterion, spellLineIndicatorMap) => ({
    ...criterion,
    spellLineIndicator: criterion?.spellCriterionSig
        ? spellLineIndicatorMap?.get?.(criterion.spellCriterionSig)
        : null,
});

const renderSummaryIcon = (criterion) => (
    <CriteriaIconImg
        key={criterion.key}
        src={criterion?.icon}
        spark={criterion?.spark}
        className={iconClassName(criterion)}
        fallback={(
            <span className="mobile-sheet-criteria__fallback">
                {(criterion?.label || '?').slice(0, 1)}
            </span>
        )}
    />
);

// Collapsed peek summary: a non-interactive strip of the selected criteria
// icons. Unlike the desktop chips, these are a read-only glance — no remove
// affordance. When the icons can't all fit the available width, the ones that
// overflow are dropped and a "+N" chip is appended so the count stays legible.
export const SheetCriteriaSummary = ({
    activeCriteria = [],
    lang = 'fr',
}) => {
    const wrapRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(activeCriteria.length);
    const total = activeCriteria.length;
    // Re-measure whenever the set of criteria changes (identity-insensitive to
    // avoid re-subscribing every render).
    const sig = activeCriteria.map((c) => c.key).join('|');

    useLayoutEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap) return undefined;
        const recompute = () => {
            const measure = wrap.querySelector('.mobile-sheet-summary__measure');
            const iconsBox = measure?.querySelector('.mobile-sheet-summary__icons');
            if (!iconsBox) return;
            const icons = [...iconsBox.children];
            if (!icons.length) { setVisibleCount(0); return; }
            const available = wrap.clientWidth;
            const firstLeft = icons[0].getBoundingClientRect().left;
            // Right edge of each icon relative to the run start (includes the
            // inter-icon gaps and any per-icon margins).
            const rightEdges = icons.map((el) => el.getBoundingClientRect().right - firstLeft);
            const runWidth = rightEdges[rightEdges.length - 1];
            if (runWidth <= available) { setVisibleCount(icons.length); return; }
            // Overflowing: reserve room for the "+N" chip (gap + its width) and
            // keep as many leading icons as still fit.
            const badge = measure.querySelector('.mobile-sheet-summary__more');
            const gap = parseFloat(getComputedStyle(iconsBox).columnGap) || 0;
            const badgeReserve = (badge ? badge.getBoundingClientRect().width : 0) + gap;
            let count = 0;
            for (let i = 0; i < rightEdges.length; i += 1) {
                if (rightEdges[i] + badgeReserve <= available) count = i + 1;
                else break;
            }
            setVisibleCount(count);
        };
        recompute();
        const ro = new ResizeObserver(recompute);
        ro.observe(wrap);
        return () => ro.disconnect();
    }, [sig]);

    if (!total) {
        return (
            <span className="mobile-sheet-summary row" ref={wrapRef}>
                <span className="mobile-sheet-summary__empty">{TXT_EMPTY[lang] || TXT_EMPTY.en}</span>
            </span>
        );
    }

    const shown = Math.max(0, Math.min(visibleCount, total));
    const hidden = total - shown;

    return (
        <span className="mobile-sheet-summary row" ref={wrapRef}>
            {/* Hidden measurement copy: always the full icon set + a sample chip,
                laid out off-flow so we can size the real strip against it. */}
            <span className="mobile-sheet-summary__measure" aria-hidden="true">
                <span className="mobile-sheet-summary__icons row">
                    {activeCriteria.map(renderSummaryIcon)}
                </span>
                <span className="mobile-sheet-summary__more">+{total}</span>
            </span>
            <span className="mobile-sheet-summary__icons row" aria-hidden="true">
                {activeCriteria.slice(0, shown).map(renderSummaryIcon)}
                {hidden > 0 ? (
                    <span className="mobile-sheet-summary__more">+{hidden}</span>
                ) : null}
            </span>
        </span>
    );
};

// Expanded sheet body: one row per criterion — icon, name, spell-line/target
// info, and a remove button.
export const SheetCriteriaList = ({
    activeCriteria = [],
    lang = 'fr',
    spellLineIndicatorMap = null,
    disabled = false,
    showWeights = false,
    onToggleWeights = null,
}) => {
    if (!activeCriteria.length) {
        return <div className="mobile-sheet-list__empty">{TXT_EMPTY[lang] || TXT_EMPTY.en}</div>;
    }
    const removeLabel = TXT_REMOVE[lang] || TXT_REMOVE.en;
    return (
        <ul className="mobile-sheet-list column">
            {activeCriteria.map((raw) => {
                const criterion = withLineIndicator(raw, spellLineIndicatorMap);
                const isStat = criterion.kind === 'stat';
                const parsed = splitCriterionLabel(criterion);
                const name = isStat ? (criterion.name || parsed.name) : parsed.name;
                return (
                    <li key={criterion.key} className="mobile-sheet-list__row row">
                        <span className="mobile-sheet-list__icon-slot">
                            <CriteriaIconImg
                                src={criterion.icon}
                                className={iconClassName(criterion)}
                                aria-hidden="true"
                                fallback={(
                                    <span className="mobile-sheet-criteria__fallback" aria-hidden="true">
                                        {(name || '?').slice(0, 1)}
                                    </span>
                                )}
                            />
                        </span>
                        <span className="mobile-sheet-list__label">{name}</span>
                        {isStat ? (
                            <StatTargetEditor criterion={criterion} lang={lang} disabled={disabled} />
                        ) : (
                            <>
                                {criterion.spellLineIndicator ? (
                                    <span className="mobile-sheet-list__line">{criterion.spellLineIndicator}</span>
                                ) : null}
                                {parsed.target ? (
                                    <span className="mobile-sheet-list__target">{parsed.target}</span>
                                ) : null}
                            </>
                        )}
                        {showWeights && criterion.onSetWeight ? (
                            <span className="mobile-sheet-list__weight">
                                <CriterionWeight
                                    value={criterion.weight}
                                    onChange={criterion.onSetWeight}
                                    disabled={disabled}
                                    lang={lang}
                                />
                            </span>
                        ) : null}
                        <button
                            type="button"
                            className="mobile-sheet-list__remove"
                            onClick={criterion.onRemove}
                            disabled={disabled || !criterion.onRemove}
                            aria-label={`${removeLabel} ${name}`.trim()}
                        >
                            <Close fontSize="small" />
                        </button>
                    </li>
                );
            })}
            {/* Display option, not a criterion — but it lives in the same list so
                it inherits the row dividers and the last-row border reset. Off by
                default: the weight fields are an advanced control and the sheet
                should read simply until they are asked for. */}
            {onToggleWeights ? (
                <li className="mobile-sheet-list__row mobile-sheet-list__row--option row">
                    <span className="mobile-sheet-list__icon-slot">
                        <WeightIcon className="mobile-sheet-list__option-glyph" />
                    </span>
                    <span className="mobile-sheet-list__label">
                        {TXT_DISPLAY_WEIGHTS[lang] || TXT_DISPLAY_WEIGHTS.en}
                    </span>
                    <span className="mobile-sheet-list__option-control">
                        <input
                            type="checkbox"
                            checked={showWeights}
                            onChange={(e) => onToggleWeights(e.target.checked)}
                            disabled={disabled}
                            aria-label={TXT_DISPLAY_WEIGHTS[lang] || TXT_DISPLAY_WEIGHTS.en}
                        />
                    </span>
                </li>
            ) : null}
        </ul>
    );
};
